import React, { useCallback, useState, useEffect } from 'react';

import Header from './components/Header';
import Grid from './components/Grid';
import ScoreDisplay from './components/ScoreDisplay';
import ProtestModal from './components/ProtestModal';
import CalculatingLoader from './components/CalculatingLoader';
import ModalManager from './components/ModalManager';
import LeaderboardView from './features/leaderboard/LeaderboardView';
import { generateUsername } from './utils/generateUsername';
import { useDailyPuzzle } from './hooks/useDailyPuzzle';
import { useGameState, CELL_STATES, GAME_PHASES } from './hooks/useGameState';
import { useScoring } from './hooks/useScoring';
import { useLeaderboard } from './hooks/useLeaderboard';
import { useAuth } from './hooks/useAuth';
import { GRID_SIZE, ENABLE_PROTESTS } from './constants/config';
import { USERNAME_KEY, PLAYED_KEY } from './constants/storageKeys';

function App() {
  const puzzle = useDailyPuzzle();
  const game = useGameState();
  const { score } = useScoring(puzzle);
  const { submit } = useLeaderboard(puzzle);
  const auth = useAuth();
  const [username, setUsername] = useState(() => localStorage.getItem(USERNAME_KEY) || '');

  useEffect(() => {
    if (auth.user?.displayName) {
      setUsername(auth.user.displayName);
      localStorage.setItem(USERNAME_KEY, auth.user.displayName);
    }
  }, [auth.user]);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [scoringError, setScoringError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [focusedCell, setFocusedCell] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(() => !localStorage.getItem(PLAYED_KEY));
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  const findNextEmptyCell = useCallback((afterRow, afterCol) => {
    for (let i = 0; i < GRID_SIZE * GRID_SIZE; i++) {
      const idx = (afterRow * GRID_SIZE + afterCol + 1 + i) % (GRID_SIZE * GRID_SIZE);
      const r = Math.floor(idx / GRID_SIZE);
      const c = idx % GRID_SIZE;
      const cell = game.grid[r][c];
      if (cell.state === CELL_STATES.EMPTY || cell.state === CELL_STATES.RETRYING) {
        return { row: r, col: c };
      }
    }
    return null;
  }, [game.grid]);

  const handleSubmitWord = useCallback(
    async (row, col, word, cellState) => {
      if (cellState === CELL_STATES.RETRYING) {
        game.startResubmitting(row, col);
      } else {
        game.startSubmitting(row, col);
      }

      const next = findNextEmptyCell(row, col);
      if (next) setFocusedCell(next);

      const rowAdj = puzzle.rowAdjectives[row];
      const colAdj = puzzle.colAdjectives[col];
      try {
        const result = await score(word, rowAdj, colAdj);
        setScoringError(null);
        game.submitAttempt(row, col, word, result.score, result.explanation, result.adj1Score, result.adj2Score);
      } catch (err) {
        console.warn('Scoring failed:', err.message);
        game.failSubmitting(row, col);
        // The API returns a short, user-facing reason for validation
        // rejections (e.g. "That doesn't look like a real word"); show it
        // so a rejected word explains itself instead of silently clearing.
        const msg = err.message && err.message.length < 80
          ? err.message
          : 'Scoring unavailable. Try again.';
        setScoringError(msg);
        setTimeout(() => setScoringError(null), 4000);
      }
    },
    [game, puzzle, score, findNextEmptyCell]
  );

  const handleRetry = useCallback(
    (row, col) => game.retryAttempt(row, col),
    [game]
  );

  const handleFinalSubmit = useCallback(async (overrideGrid) => {
    setSubmitError(null);
    const gridToSubmit = overrideGrid || game.grid;

    let name;
    if (auth.isAuthenticated && auth.user?.displayName) {
      name = auth.user.displayName;
    } else if (username) {
      name = username;
    } else {
      name = generateUsername(gridToSubmit);
    }
    setUsername(name);
    localStorage.setItem(USERNAME_KEY, name);

    game.submitFinal();
    const totalScore = gridToSubmit.flat().reduce((sum, cell) => sum + (cell.finalScore || 0), 0);
    const cells = gridToSubmit.flat().map((c) => ({
      word: c.finalWord,
      score: c.finalScore,
      adj1Score: c.finalAdj1Score ?? null,
      adj2Score: c.finalAdj2Score ?? null,
    }));

    const protestResult = game.protestResults.length > 0
      ? {
          accepted: game.protestResults.some(p => p.accepted),
          scoreChange: game.protestResults.reduce((sum, p) => sum + p.scoreChange, 0),
        }
      : null;

    try {
      const data = await submit(cells, totalScore, name, game.retryCount, protestResult);
      if (auth.isAuthenticated) auth.refreshUser();
      game.showResults(data);
    } catch (err) {
      console.error('Final submit failed:', err);
      setSubmitError('Submission failed. Please try again.');
      game.recoverFromSubmitError();
    }
  }, [game, submit, username, auth]);

  const handleSubmitClick = useCallback(() => {
    const allScored = game.grid.flat().every((c) => c.state === CELL_STATES.FINALIZED);
    if (game.gamePhase === GAME_PHASES.ALL_FILLED || allScored) {
      handleFinalSubmit();
    } else {
      setShowConfirmSubmit(true);
    }
  }, [game, handleFinalSubmit]);

  const handleConfirmEarlySubmit = useCallback(() => {
    setShowConfirmSubmit(false);
    const finalizedGrid = game.submitEarly();
    handleFinalSubmit(finalizedGrid);
  }, [game, handleFinalSubmit]);

  const handleCloseHowToPlay = useCallback(() => {
    setShowHowToPlay(false);
    localStorage.setItem(PLAYED_KEY, '1');
  }, []);

  const headerProps = {
    date: puzzle.date,
    puzzleNumber: puzzle.puzzleNumber,
    user: auth.user,
    onSignIn: () => auth.setShowAuthModal(true),
    onSignOut: auth.signOut,
    onProfile: () => setShowProfileModal(true),
    onHowToPlay: () => setShowHowToPlay(true),
  };

  // Loading state
  if (puzzle.isLoading) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-black text-ink mb-2">Approximate Grid</h1>
        <hr className="border-t-2 border-ink w-20 mb-4" />
        {puzzle.puzzleNumber && (
          <p className="text-ink-faint font-body text-sm mb-4">Puzzle #{puzzle.puzzleNumber}</p>
        )}
        <p className="text-ink-light font-body">Preparing today's puzzle...</p>
      </div>
    );
  }

  const isScoring = game.grid.flat().some(
    (c) => c.state === CELL_STATES.SUBMITTING || c.state === CELL_STATES.RESUBMITTING
  );

  const totalScore = game.getTotalScore();

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4 relative">
      <Header {...headerProps} />

      {game.gamePhase === GAME_PHASES.FINAL && (
        <LeaderboardView
          puzzle={puzzle}
          totalScore={totalScore}
          username={username}
          grid={game.grid}
          retryCount={game.retryCount}
          protestResults={game.protestResults}
        />
      )}

      {game.gamePhase === GAME_PHASES.RESULTS && (
        <ScoreDisplay
          grid={game.grid}
          totalScore={totalScore}
          leaderboardData={game.leaderboardData}
          onProtest={(row, col) => game.startProtest(row, col)}
          onFinish={() => game.finishGame()}
          puzzle={puzzle}
          retryCount={game.retryCount}
          protestResults={game.protestResults}
        />
      )}

      {ENABLE_PROTESTS && game.gamePhase === GAME_PHASES.PROTESTING && game.protestCell && (
        <ProtestModal
          cell={game.grid[game.protestCell.row][game.protestCell.col]}
          row={game.protestCell.row}
          col={game.protestCell.col}
          puzzle={puzzle}
          onResolve={(accepted, newScore, explanation) => game.resolveProtest(accepted, newScore, explanation)}
          onCancel={game.cancelProtest}
        />
      )}

      {game.gamePhase === GAME_PHASES.SUBMITTING_FINAL && (
        <CalculatingLoader />
      )}

      {(game.gamePhase === GAME_PHASES.PLAYING || game.gamePhase === GAME_PHASES.ALL_FILLED) && (
        <>
          <Grid
            puzzle={puzzle}
            grid={game.grid}
            onSubmitWord={handleSubmitWord}
            onRetry={handleRetry}
            canRetry={game.canRetry}
            CELL_STATES={CELL_STATES}
            focusedCell={focusedCell}
            onCellFocus={setFocusedCell}
          />
          {game.canRetry && (2 - game.retryCount) > 0 && (
            <p className="mt-2 text-xs text-ink-faint font-body">
              retries: {'\uD83D\uDD04'.repeat(2 - game.retryCount)}
            </p>
          )}
          {(scoringError || submitError) && (
            <p className="mt-3 text-sm text-red-600 font-body font-medium">
              {scoringError || submitError}
            </p>
          )}
          {isScoring && (
            <p className="mt-3 text-sm text-ink-faint font-body italic">
              You can enter another answer while we are thinking
            </p>
          )}
          <button
            onClick={handleSubmitClick}
            className={`mt-4 sm:mt-8 px-6 sm:px-8 py-3 sm:py-4 text-lg sm:text-xl font-serif font-bold border-2 border-ink transition-all ${
              game.gamePhase === GAME_PHASES.ALL_FILLED
                ? 'bg-ink text-cream hover:bg-ink-light shadow-md'
                : 'bg-white text-ink hover:bg-cream'
            }`}
          >
            Submit Grid
          </button>
          {showConfirmSubmit && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="bg-white border-2 border-ink p-8 max-w-sm mx-4">
                <p className="text-lg font-serif font-bold text-ink text-center mb-2">
                  Submit with empty cells?
                </p>
                <p className="text-sm font-body text-ink-light text-center mb-6">
                  Unfilled squares will score 0.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowConfirmSubmit(false)}
                    className="px-5 py-2 border-2 border-ink text-ink font-serif font-bold hover:bg-cream transition-all"
                  >
                    Keep playing
                  </button>
                  <button
                    onClick={handleConfirmEarlySubmit}
                    className="px-5 py-2 bg-ink text-cream font-serif font-bold border-2 border-ink hover:bg-ink-light transition-all"
                  >
                    Submit anyway
                  </button>
                </div>
              </div>
            </div>
          )}
          <p className="mt-6 text-xs text-ink-faint font-body">
            Adjectives sourced from{' '}
            <a
              href="https://www.merriam-webster.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-ink-light"
            >
              Merriam-Webster
            </a>
          </p>
        </>
      )}

      <ModalManager
        auth={auth}
        showProfileModal={showProfileModal}
        onCloseProfile={() => setShowProfileModal(false)}
        showHowToPlay={showHowToPlay}
        onCloseHowToPlay={handleCloseHowToPlay}
      />
    </div>
  );
}

export default App;
